// External packages
import * as React from 'react';
import { z } from 'zod';

// Components
import { Button, ButtonIcon, PlainButton } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Forms';
import { Icon } from 'components/Icons';
import * as Modal from 'components/Modal';
import { SearchField } from 'components/SearchField';
import { Option, Select } from 'components/Select';
import { SortableList } from 'components/SortableList';
import { SvgViewer } from 'components/SvgViewer';
import { Tab, TabList, TabPanel, Tabs } from 'components/Tabs';
import TiptapEditor from 'components/TiptapEditor';

// Modules
import { BulkActionsToolbar } from 'modules/BulkActionsToolbar';
import { ImagePreviewModal } from 'modules/images/ImagePreviewModal';
import { PlaceModal } from 'modules/places/PlaceModal';
import { VisionModelManager } from 'modules/places/VisionModelManager';

// Hooks
import {
  useDeletePlace,
  useGetPlaces,
  useReorderPlaceExhibits,
  useUpdatePlace,
} from 'api/place';
import { useBulkSelection } from 'hooks/useBulkSelection';
import { useTranslationSelection } from 'hooks/useTranslationSelection';

// Types
import { Place } from '../../../../server/src/types/place.types';

// Utils
import { filterPlaces } from 'utils/filterPlaces';
import { sortPlaces } from 'utils/sortPlaces';
import { useNavigate } from 'react-router-dom';
import { getBestImageUrl } from 'utils/getImageUrl';

// Schemas
import { placeCreateSchema } from 'schemas/place';

export const PlacesList = () => {
  const [search, setSearch] = React.useState('');
  const [sortBy, setSortBy] = React.useState<string>('datum');
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    React.useState(false);

  const { data: places, isLoading, isError } = useGetPlaces();
  const { mutate: deletePlace, isPending: isDeleting } = useDeletePlace();

  const filteredPlaces = React.useMemo(
    () => filterPlaces(places, search),
    [places, search]
  );

  const sortedPlaces = React.useMemo(
    () => sortPlaces(filteredPlaces, sortBy),
    [filteredPlaces, sortBy]
  );

  const bulkSelection = useBulkSelection(places);

  const handleBulkDelete = () => {
    bulkSelection.selectedIds.forEach((id) => {
      deletePlace(id);
    });
    bulkSelection.clearSelection();
    setDeleteConfirmationModalOpen(false);
  };

  return (
    <>
      {places && places.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Pretraži sobe..."
            />
            <Select
              placeholder="Sortiraj po"
              className="w-auto"
              selectedKey={sortBy}
              onSelectionChange={(key) => setSortBy(key as string)}
            >
              <Option id="naziv">Naziv (A-B)</Option>
              <Option id="naziv-desc">Naziv (Z-A)</Option>
              <Option id="datum">Dodano (najnovije)</Option>
              <Option id="datum-desc">Dodano (najstarije)</Option>
            </Select>
          </div>

          <div className="mt-4">
            <BulkActionsToolbar
              allSelected={!!bulkSelection.allSelected}
              someSelected={!!bulkSelection.someSelected}
              selectedCount={bulkSelection.selectedIds.length}
              onSelectAll={bulkSelection.handleSelectAll}
              onBulkDelete={handleBulkDelete}
              isDeleteModalOpen={deleteConfirmationModalOpen}
              setDeleteModalOpen={setDeleteConfirmationModalOpen}
              isDeleting={isDeleting}
              label="Odaberi sve"
            />
          </div>
        </>
      )}

      {isLoading || isError || !sortedPlaces || sortedPlaces.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          {isLoading ? (
            <Icon name="loader" className="animate-spin size-10" />
          ) : isError ? (
            <p>Greška pri učitavanju soba.</p>
          ) : (
            <p>Nema pronađenih soba.</p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4 overflow-y-auto">
          {sortedPlaces.map((place: Place) => (
            <PlacesListItem
              key={place.id}
              place={place}
              isSelected={bulkSelection.selectedIds.includes(place.id)}
              onChange={(checked) =>
                bulkSelection.handleSelect(place.id, !!checked)
              }
            />
          ))}
        </div>
      )}
    </>
  );
};

export const PlacesListItem: React.FC<{
  place: Place;
  isSelected?: boolean;
  onChange?: (checked: boolean) => void | undefined;
}> = ({ place, isSelected, onChange }) => {
  const navigate = useNavigate();
  const [placeUpdateModalOpen, setPlaceUpdateModalOpen] = React.useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] =
    React.useState(false);

  const translationSelection = useTranslationSelection(place.translations);
  const { mutate: updatePlace, isPending: isUpdating } = useUpdatePlace();
  const { mutate: reorderExhibits, isPending: isReordering } =
    useReorderPlaceExhibits();

  const [isReorderingExhibits, setIsReorderingExhibits] = React.useState(false);
  const [draftExhibits, setDraftExhibits] = React.useState(place.exhibits);

  React.useEffect(() => {
    setDraftExhibits(place.exhibits);
    setIsReorderingExhibits(false);
  }, [place.exhibits]);

  const exhibitsOrderChanged = React.useMemo(
    () =>
      draftExhibits.length !== place.exhibits.length ||
      draftExhibits.some((e, i) => place.exhibits[i]?.id !== e.id),
    [draftExhibits, place.exhibits]
  );

  const handleSaveExhibitOrder = () => {
    reorderExhibits(
      {
        placeId: place.id,
        exhibitIds: draftExhibits.map((e) => e.id),
      },
      {
        onSuccess: () => setIsReorderingExhibits(false),
      }
    );
  };

  const handleCancelExhibitOrder = () => {
    setDraftExhibits(place.exhibits);
    setIsReorderingExhibits(false);
  };

  const handleUpdatePlace = (
    data: z.infer<typeof placeCreateSchema>,
    id: string
  ) => {
    updatePlace(
      { id, data },
      {
        onSuccess: () => setPlaceUpdateModalOpen(false),
      }
    );
  };

  return (
    <div
      key={place.id}
      className="p-4 border min-w-fit border-gray-200 bg-white rounded-lg"
    >
      <div className="flex justify-between gap-8">
        <Checkbox
          isSelected={isSelected}
          onChange={(checked) => onChange?.(!!checked)}
          className="self-start"
        />
        <div className="flex gap-2">
          <PlaceModal
            onSubmit={(data) => handleUpdatePlace(data, place.id)}
            defaultValues={{
              name: place.name || undefined,
              translations: place.translations.map((t) => ({
                languageCode: t.languageCode,
                title: t.title,
                description: t.description,
                shortDescription: t.shortDescription || '',
                videoIds: t.videoIds || [],
                audioIds: t.audioIds || [],
              })),
              imageIds: place.imageIds || [],
              weeklyAvailability: place.weeklyAvailability || undefined,
              mapId: place.mapId || undefined,
            }}
            modalTrigger={
              <Button variant="ghost" icon={<Icon name="edit" />}>
                Uredi
              </Button>
            }
            isOpen={placeUpdateModalOpen}
            onOpenChange={setPlaceUpdateModalOpen}
            isPending={isUpdating}
          />
          <Select
            selectedKey={translationSelection.selectedLanguage}
            onSelectionChange={(key) => {
              translationSelection.setSelectedLanguage(key as string);
            }}
            items={translationSelection.languageOptions}
          >
            {translationSelection.languageOptions.map((item) => (
              <Option key={item.key} id={item.key}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <h2 className="text-xl mb-4">{place.name}</h2>
      <Tabs className="min-w-fit">
        <TabList>
          <Tab id="detalji" className="flex-1 text-center">
            Detalji
          </Tab>
          <Tab id="radno-vrijeme" className="flex-1 text-center">
            Radno vrijeme
          </Tab>
          <Tab id="media" className="flex-1 text-center">
            Media
          </Tab>
          <Tab id="eksponati" className="flex-1 text-center">
            Eksponati
          </Tab>
          <Tab id="mapa" className="flex-1 text-center">
            Mapa
          </Tab>
          <Tab id="ai-model" className="flex-1 text-center">
            AI model
          </Tab>
          <Tab id="dodatno" className="flex-1 text-center">
            Dodatno
          </Tab>
        </TabList>
        <TabPanel id="detalji" className="px-4 py-2">
          <div className="space-y-4">
            <div>
              <Label className="font-bold">Naslov</Label>
              <p>{translationSelection.translation.title || '-'}</p>
            </div>
            <div>
              <Label className="font-bold">Kratki Opis</Label>
              {translationSelection.translation.shortDescription ? (
                /<[a-z][\s\S]*>/i.test(
                  translationSelection.translation.shortDescription
                ) ? (
                  <TiptapEditor
                    disabled
                    content={translationSelection.translation.shortDescription}
                  />
                ) : (
                  <p>{translationSelection.translation.shortDescription}</p>
                )
              ) : (
                <p>-</p>
              )}
            </div>
            <div>
              <Label className="font-bold">Opis</Label>
              {translationSelection.translation.description ? (
                <>
                  <div
                    className={
                      isDescriptionExpanded &&
                      !/<[a-z][\s\S]*>/i.test(
                        translationSelection.translation.description
                      )
                        ? ''
                        : 'line-clamp-3'
                    }
                  >
                    {/<[a-z][\s\S]*>/i.test(
                      translationSelection.translation.description
                    ) ? (
                      <TiptapEditor
                        disabled
                        content={translationSelection.translation.description}
                      />
                    ) : (
                      <p>{translationSelection.translation.description}</p>
                    )}
                  </div>
                  {translationSelection.translation.description.length > 120 &&
                    !/<[a-z][\s\S]*>/i.test(
                      translationSelection.translation.description
                    ) && (
                      <PlainButton
                        className="text-blue-600 cursor-pointer transition-all hover:underline mt-1 text-xs"
                        onPress={() => setIsDescriptionExpanded((v) => !v)}
                      >
                        {isDescriptionExpanded
                          ? 'Prikaži manje'
                          : 'Prikaži više'}
                      </PlainButton>
                    )}
                </>
              ) : (
                <p>-</p>
              )}
            </div>
          </div>

          <hr className="text-gray-200 my-4" />
          <div className="flex justify-between gap-4 flex-wrap">
            <div className="flex-1 flex gap-2 items-center text-gray-600">
              <Icon name="language" />
              <p className="text-xs">
                {place.translations.length} jezik
                {place.translations.length > 1 ||
                place.translations.length === 0
                  ? 'a'
                  : ''}
              </p>
            </div>
            <div className="flex-1 flex gap-2 items-center text-gray-600">
              <Icon name="image" />
              <p className="text-xs">{place.images.length} slika</p>
            </div>
            <div className="flex-1 flex gap-2 items-center text-gray-600">
              <Icon name="building" />
              <p className="text-xs">
                {place.exhibits.length} eksponat
                {place.exhibits.length > 1 || place.exhibits.length === 0
                  ? 'a'
                  : ''}
              </p>
            </div>
          </div>
        </TabPanel>
        <TabPanel id="radno-vrijeme" className="px-4 py-2">
          {place.weeklyAvailability &&
          Object.entries(place.weeklyAvailability).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(place.weeklyAvailability).map(([day, slots]) => (
                <div key={day} className="flex gap-4 last:!mb-0">
                  <p className="flex-1 text-gray-600">
                    <strong>{day[0].toUpperCase() + day.slice(1)}</strong>
                  </p>
                  <div className="flex-3 text-gray-600">
                    {Array.isArray(slots) &&
                      slots.length > 0 &&
                      slots.map(
                        (
                          slot: {
                            startTime: string;
                            endTime: string;
                          },
                          idx: number
                        ) => (
                          <p key={idx} className="">
                            {slot.startTime} - {slot.endTime}
                          </p>
                        )
                      )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">Nema radnog vremena.</p>
          )}
        </TabPanel>
        <TabPanel id="media" className="px-4 py-2">
          <Label>Slike</Label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {place.images.length > 0 ? (
              place.images.map((image) => (
                <div
                  key={image.id}
                  className="border aspect-image col-span-1 border-gray-200 rounded-lg overflow-hidden"
                >
                  <img
                    src={getBestImageUrl(image, 'small')}
                    alt={image.altText || 'Slika'}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))
            ) : (
              <p className="text-gray-400 col-span-full">Nema slika.</p>
            )}
          </div>
          <Label className="mt-4">
            Videa ({translationSelection.translation.languageCode.toUpperCase()}
            )
          </Label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {translationSelection.translation.videos.length > 0 ? (
              translationSelection.translation.videos.map((video) => (
                <div
                  key={video.id}
                  className="border col-span-1 border-gray-200 rounded-lg overflow-hidden"
                >
                  <video
                    width="320"
                    height="240"
                    muted
                    className="h-full w-full object-cover"
                  >
                    <source src={video.url} type="video/mp4" />
                    <source src={video.url} type="video/ogg" />
                    Vaš preglednik ne podržava video tag.
                  </video>
                </div>
              ))
            ) : (
              <p className="text-gray-400 col-span-full">Nema video zapisa.</p>
            )}
          </div>
          <Label className="mt-4">
            Audio ({translationSelection.translation.languageCode.toUpperCase()}
            )
          </Label>
          <div className="flex flex-col gap-4">
            {translationSelection.translation.audios.length > 0 ? (
              translationSelection.translation.audios.map((audio) => (
                <div key={audio.id}>
                  <audio controls className="w-full flex-3 min-w-75 shrink-0">
                    <source src={audio.url} type={audio.mimeType} />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              ))
            ) : (
              <p className="text-gray-400 col-span-full">Nema audio zapisa.</p>
            )}
          </div>
        </TabPanel>
        <TabPanel id="eksponati" className="px-4 py-2">
          {place.exhibits.length > 0 ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-500">
                  {isReorderingExhibits
                    ? 'Povuci eksponate da promijeniš redoslijed prikaza.'
                    : 'Redoslijed kojim se eksponati prikazuju u aplikaciji.'}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {isReorderingExhibits ? (
                    <>
                      <Button
                        variant="ghost"
                        colorScheme="secondary"
                        onPress={handleCancelExhibitOrder}
                        isDisabled={isReordering}
                      >
                        Odustani
                      </Button>
                      <Button
                        onPress={handleSaveExhibitOrder}
                        isPending={isReordering}
                        isDisabled={!exhibitsOrderChanged}
                        icon={<Icon name="check" />}
                      >
                        Spremi redoslijed
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      onPress={() => setIsReorderingExhibits(true)}
                      isDisabled={place.exhibits.length < 2}
                    >
                      Promijeni redoslijed
                    </Button>
                  )}
                </div>
              </div>

              {isReorderingExhibits ? (
                <SortableList
                  items={draftExhibits}
                  onChange={setDraftExhibits}
                  isDisabled={isReordering}
                  renderItem={(exhibit, { index }) => (
                    <div className="flex items-center gap-4">
                      <span className="w-6 shrink-0 text-sm font-semibold text-gray-400">
                        {index + 1}.
                      </span>
                      <div className="min-w-0 flex-1">
                        <Label className="font-bold">Naziv</Label>
                        <p className="truncate text-gray-600">
                          {exhibit.name || '—'}
                        </p>
                      </div>
                      <div className="hidden min-w-0 flex-1 sm:block">
                        <Label className="font-bold">ID</Label>
                        <p className="truncate text-xs text-gray-500">
                          {exhibit.id}
                        </p>
                      </div>
                    </div>
                  )}
                />
              ) : (
                <div className="space-y-2">
                  {place.exhibits.map((exhibit, index) => (
                    <div
                      className="flex gap-4 items-center border-b last:border-0 border-gray-300 pb-2"
                      key={exhibit.id}
                    >
                      <span className="w-6 shrink-0 text-sm font-semibold text-gray-400">
                        {index + 1}.
                      </span>
                      <div className="flex-1">
                        <Label className="font-bold">ID</Label>
                        <p className="text-gray-600">{exhibit.id}</p>
                      </div>
                      <div className="flex-1">
                        <Label className="font-bold">Naziv</Label>
                        <p className="text-gray-600">{exhibit.name}</p>
                      </div>
                      <div className="shrink-0">
                        <Modal.TriggerRoot>
                          <ButtonIcon
                            variant="outline"
                            isDisabled={!exhibit.images.length}
                          >
                            <Icon name="image" />
                          </ButtonIcon>
                          <Modal.Overlay isDismissable>
                            <Modal.Root>
                              <Modal.Dialog>
                                <Modal.Close />
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:grid-cols-5">
                                  {exhibit.images.map((image) => (
                                    <ImagePreviewModal
                                      image={image}
                                      key={image.id}
                                      modalTrigger={
                                        <PlainButton className="cursor-pointer rounded-lg">
                                          <div className="overflow-hidden group aspect-image rounded-lg">
                                            <img
                                              className="h-full w-full object-cover transition-transform group-hover:scale-110"
                                              src={getBestImageUrl(
                                                image,
                                                'small'
                                              )}
                                              alt={image.altText || ''}
                                            />
                                          </div>
                                        </PlainButton>
                                      }
                                      disableDelete
                                    />
                                  ))}
                                </div>
                              </Modal.Dialog>
                            </Modal.Root>
                          </Modal.Overlay>
                        </Modal.TriggerRoot>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-400">Nema eksponata.</p>
          )}
        </TabPanel>
        <TabPanel id="mapa" className="px-4 py-2">
          {place.map && (
            <div className="w-full">
              <SvgViewer
                url={place.map.url}
                altText={place.map.altText || 'Mapa'}
                width={800}
                height={600}
                className="w-full"
                exhibits={
                  place.exhibits
                    ?.filter((exhibit) => exhibit.position)
                    ?.map((exhibit) => ({
                      id: exhibit.id,
                      position: exhibit.position!,
                      title: exhibit.name || undefined,
                      onClick: () => {
                        navigate(`/eksponati/${exhibit.id}`);
                      },
                    })) || []
                }
              />
            </div>
          )}
        </TabPanel>
        <TabPanel id="ai-model" className="px-4 py-2">
          <VisionModelManager placeId={place.id} />
        </TabPanel>
        <TabPanel id="dodatno" className="px-4 py-2">
          <div className="flex flex-wrap gap-8">
            <div className="flex-1">
              <Label className="font-bold mb-6">Dodatne informacije</Label>
              <div className="space-y-2">
                <div className="flex gap-4">
                  <p className="text-gray-600 flex-1">ID:</p>
                  <p className="flex-2 text-end font-medium break-all">
                    {place.id}
                  </p>
                </div>
                <div className="flex gap-4">
                  <p className="text-gray-600 flex-1">Kreirano:</p>
                  <p className="flex-2 text-end">
                    {new Date(place.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-4">
                  <p className="text-gray-600 flex-1">Ažurirano:</p>
                  <p className="flex-2 text-end">
                    {new Date(place.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <Label className="font-bold mb-6">Dostupni jezici</Label>

              {place.translations.map((translation) => (
                <div
                  key={translation.id}
                  className="border items-start not-last:mb-2 border-gray-400 p-2 flex gap-2"
                >
                  <div className="text-white h-6 px-3 flex items-center justify-center text-xs bg-black rounded-full uppercase">
                    <p>{translation.languageCode}</p>
                  </div>

                  <p>{translation.title}</p>
                </div>
              ))}
            </div>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
};
